import { ChangeDetectionStrategy, Component, inject, Injector, OnInit, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FieldType, FormlyModule, type FormlyFieldConfig } from '@ngx-formly/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLabel } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { startWith } from 'rxjs';

import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { IDENTITY_DOCUMENT_TYPE_OPTIONS } from './fieldGroup.config';
import { commitUserValue } from './user-edit.util';

interface TypeOption {
  label: string;
  value: string;
}

/**
 * Composite `identity-document` field: document type + number as ONE 37px input, read the same
 * way on every surface ("ID-kaart ••••1234"). The value is `{ type, number }` from a static
 * `fieldGroup` (IDENTITY_DOCUMENT_FIELDS, fieldGroup.config.ts), like `name`/`address`.
 *
 * Only the `number` subfield is rendered through Formly (`<formly-field>` → secure-input: vault
 * write/read, masked reminder, eye button). The `type` subfield keeps its FormControl, validation
 * and form-builder affordances (required inheritance, "Subvelden verbergen"), but is rendered
 * here as a neutral CHIP that opens a light `mat-menu` — a `custom-select` with its clear button
 * and 152px column read as two stacked fields instead of one answer (design review 2026-09-24,
 * Claude Design canvas "Identiteitsdocument-veld"). The chip is the same shape as the tags chip,
 * in the neutral badge tokens: brand purple is for selected states only (see
 * figma-brand-accent-only-rule), and a prefix choice is not a state.
 */
@Component({
  selector: 'formly-field-identity-document',
  template: `
    <div class="dd-identity-document" role="group" [attr.aria-labelledby]="labelId">
      <mat-label class="dd-identity-document__label" [id]="labelId">
        <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
      </mat-label>
      <div
        class="dd-identity-document__control"
        [class.dd-identity-document__control--invalid]="field.formControl.invalid && field.formControl.touched"
        [class.dd-identity-document__control--no-type]="!isTypeVisible()">
        @if (isTypeVisible()) {
          <button
            type="button"
            class="dd-doc-type-chip dd-identity-document__type"
            [class.dd-identity-document__type--empty]="!typeLabel()"
            [disabled]="isTypeDisabled()"
            [matMenuTriggerFor]="typeMenu"
            (menuClosed)="onTypeMenuClosed()"
            aria-haspopup="menu"
            [attr.aria-label]="typeAriaLabel()">
            <span class="dd-identity-document__type-label">{{ typeLabel() ?? 'Soort' }}</span>
            <mat-icon class="dd-identity-document__type-caret icon-size-3" svgIcon="feather:chevron-down" aria-hidden="true"></mat-icon>
          </button>
          <mat-menu #typeMenu="matMenu" class="dd-identity-document-menu">
            @for (option of typeOptions(); track option.value) {
              <button
                mat-menu-item
                type="button"
                class="dd-identity-document-menu__item"
                [class.dd-identity-document-menu__item--selected]="option.value === typeValue()"
                [attr.aria-label]="option.value === typeValue() ? option.label + ', geselecteerd' : option.label"
                (click)="selectType(option.value)">
                <span class="dd-identity-document-menu__lead" aria-hidden="true">
                  @if (option.value === typeValue()) {
                    <mat-icon class="icon-size-4" svgIcon="feather:check" aria-hidden="true"></mat-icon>
                  }
                </span>
                <span>{{ option.label }}</span>
              </button>
            }
          </mat-menu>
        }
        @if (numberField(); as numberField) {
          @if (!isSubfieldHidden(numberField)) {
            <!-- numberField.className carries the fixed identifier from IDENTITY_DOCUMENT_FIELDS
                 (dd-identity-document-field--number, fieldGroup.config.ts) — the specs and stories
                 scope their checks on it. -->
            <div class="dd-identity-document__number" [class]="numberField.className">
              <formly-field [field]="numberField"></formly-field>
            </div>
          }
        }
      </div>
      <!-- The number subfield renders its own <app-formly-error> next to its mat-form-field, i.e.
           inside the bordered box; that copy is hidden below (::ng-deep) and every subfield's error
           is re-rendered here, under the box. The composite's own error at the bottom cannot stand
           in for them: a FormGroup's errors hold only group-level validator results. -->
      @for (subField of field.fieldGroup; track subField.key) {
        <app-formly-error [field]="subField"></app-formly-error>
      }
      @if (field.props.description) {
        <div class="dd-identity-document__hint">{{ field.props.description }}</div>
      } @else {
        <div class="dd-identity-document__hint">Versleuteld opgeslagen; alleen de laatste tekens blijven zichtbaar.</div>
      }
    </div>
    <app-formly-error [field]="field"></app-formly-error>
  `,
  styles: `
    :host {
      display: block;
    }

    .dd-identity-document__label {
      display: block;
      margin-bottom: 4px;
    }

    /* One Figma input: 37px tall, 8px radius, the field tokens. The chip sits 6px from the left
       edge; the number input follows at the 12px inset its own mat-form-field keeps (the compact
       field rule in _forms.scss sets it with !important at a specificity no component rule
       reaches), so the row itself adds no gap. */
    .dd-identity-document__control {
      display: flex;
      align-items: center;
      gap: 0;
      height: 37px;
      box-sizing: border-box;
      padding-left: 6px;
      border: 1px solid var(--dd-field-border);
      background: var(--dd-field-bg);
      border-radius: var(--dd-field-radius);
    }

    /* "Subvelden verbergen" on the type: the number input then owns the full left edge, with its
       own 12px inset as the only lead-in — the same as a standalone field. */
    .dd-identity-document__control--no-type {
      padding-left: 0;
    }

    /* :focus-within covers both the chip and the secure-input's <input> — whichever has focus,
       the WHOLE box shows the field focus state, like a normal single-control field. */
    .dd-identity-document__control:focus-within {
      border-color: var(--dd-field-border-focus);
      box-shadow: 0 0 0 var(--dd-field-focus-ring-width, 3px) var(--dd-field-focus-ring);
    }

    .dd-identity-document__control--invalid {
      border-color: var(--dd-field-border-error);
    }

    .dd-identity-document__control--invalid:focus-within {
      box-shadow: 0 0 0 var(--dd-field-focus-ring-width, 3px) var(--dd-field-error-ring);
    }

    /* The chip's shared look (.dd-doc-type-chip) lives in _chips.scss, next to the tags chip it
       echoes. Only the trigger behaviour is added here. */
    .dd-identity-document__type {
      flex: 0 0 auto;
      cursor: pointer;
    }

    .dd-identity-document__type:disabled {
      cursor: default;
    }

    /* The chip has its own focus ring: the box ring alone would not say WHICH of the two
       controls has keyboard focus. Neutral, like every focus ring in the Figma stylesheet. */
    .dd-identity-document__type:focus-visible {
      outline: 2px solid var(--dd-field-border-focus);
      outline-offset: 1px;
    }

    .dd-identity-document__type--empty {
      color: var(--dd-field-placeholder, #545559);
    }

    .dd-identity-document__type-caret {
      flex: 0 0 auto;
    }

    .dd-identity-document__number {
      flex: 1 1 auto;
      min-width: 0;
    }

    .dd-identity-document__hint {
      margin-top: 4px;
      font-size: 0.75rem;
      color: var(--dd-field-label);
      opacity: 0.7;
    }

    /* The secure-input renders its own mat-form-field chrome (border, background, label reserve,
       hint/error reserve) — this composite draws ONE box, so all of it is stripped inside the
       number slot and the inner control fills the slot edge-to-edge. Scoped to
       .dd-identity-document__number so no other field on the page is affected. */
    :host ::ng-deep .dd-identity-document__number formly-field {
      display: block;
    }

    /* A standalone field reserves 12px under its box for hint/error text ("#top form
       mat-form-field" in _forms.scss — an id selector, hence !important); inside the box that
       reserve pushed the composite to 51px with its content sitting high. */
    :host ::ng-deep .dd-identity-document__number .mat-mdc-form-field {
      padding-bottom: 0 !important;
    }

    :host ::ng-deep .dd-identity-document__number .mat-mdc-text-field-wrapper {
      background: transparent !important;
      border: none !important;
      /* Undoes the app-wide "space for the label above the box" reserve (_forms.scss) — the label
         here is hidden, not absent, so Material still applies that class-based reserve. */
      margin-top: 0 !important;
      /* 35px inside the 37px box (1px border each side): the composite then measures exactly one
         Figma input, not 39px. */
      height: 35px !important;
      padding-left: 0 !important;
    }

    :host ::ng-deep .dd-identity-document__number .mat-mdc-form-field-flex {
      height: 35px !important;
      border: none !important;
    }

    :host ::ng-deep .dd-identity-document__number .mdc-notched-outline {
      border: none !important;
    }

    /* Hide the secure-input's own label VISUALLY ONLY — the same clip technique as Tailwind's
       .sr-only utility (document-card.component.ts, required-documents-checklist.component.ts).
       NEVER display:none / visibility:hidden here: Material renders mat-label's projected content
       inside its own <label for=control-id>, and the accessible-name algorithm excludes
       display:none/visibility:hidden descendants but NOT clipped ones — with display:none the
       input lost its accessible name entirely (verified via the real Chromium AX tree). */
    :host ::ng-deep .dd-identity-document__number mat-label {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    }

    /* Material's required-marker asterisk is a SIBLING of mat-label inside the same wrapping
       <label>, so the clip rule above does not reach it. Safe to hide with display:none: Material
       stamps aria-hidden="true" on it, and the composite's own outer label already shows the
       required/optional marker once. */
    :host ::ng-deep .dd-identity-document__number .mat-mdc-form-field-required-marker {
      display: none;
    }

    :host ::ng-deep .dd-identity-document__number .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }

    /* The secure-input's own validation error would render inside the bordered box; it is
       re-rendered under the box by this component's template instead. display:none also keeps the
       hidden copy's role="alert" out of the accessibility tree, so each error is announced once. */
    :host ::ng-deep .dd-identity-document__number app-formly-error {
      display: none;
    }
  `,
  imports: [FormlyModule, MatLabel, MatIconModule, MatMenuModule, FormlyErrorComponent, FieldLabelRowComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldIdentityDocument extends FieldType implements OnInit {
  readonly #injector = inject(Injector);

  /**
   * The type subfield's live value. Wired in ngOnInit (Formly assigns `field` after construction),
   * from the control's valueChanges so a programmatic write (hydration, reset) moves the chip
   * label too — not only the choices made through this component's own menu.
   */
  typeValue: Signal<unknown> = signal<unknown>(null);

  get labelId(): string {
    return `${this.field.id ?? this.field.key}-label`;
  }

  ngOnInit(): void {
    const control = this.typeField()?.formControl;
    if (control) {
      this.typeValue = toSignal(control.valueChanges.pipe(startWith(control.value)), {
        injector: this.#injector,
        initialValue: control.value,
      });
    }
  }

  typeField(): FormlyFieldConfig | undefined {
    return this.field.fieldGroup?.find(subField => subField.key === 'type');
  }

  numberField(): FormlyFieldConfig | undefined {
    return this.field.fieldGroup?.find(subField => subField.key === 'number');
  }

  /** The fixed list from IDENTITY_DOCUMENT_FIELDS, carried through generatedForm as static props.options. */
  typeOptions(): TypeOption[] {
    const options = this.typeField()?.props?.['options'];
    return Array.isArray(options) && options.length ? (options as TypeOption[]) : IDENTITY_DOCUMENT_TYPE_OPTIONS;
  }

  typeLabel(): string | null {
    const value = this.typeValue();
    if (value == null || value === '') {
      return null;
    }
    return this.typeOptions().find(option => option.value === value)?.label ?? String(value);
  }

  typeAriaLabel(): string {
    const label = this.typeLabel();
    return label ? `Soort document: ${label}` : 'Soort document kiezen';
  }

  isTypeVisible(): boolean {
    const typeField = this.typeField();
    return !!typeField && !this.isSubfieldHidden(typeField);
  }

  isTypeDisabled(): boolean {
    return this.props.disabled === true || this.props['readonly'] === true || !!this.typeField()?.formControl?.disabled;
  }

  /**
   * Form-builder "Subvelden verbergen" hides a subfield via a `hidden` (Tailwind display:none)
   * class on its generated `className` (`form-generate.util.mjs`'s `isHiddenSubfield`) rather
   * than Formly's own `hide`/`hideExpression` — the control must stay in the form model and keep
   * submitting its default value, so it can't be removed from `field.fieldGroup` outright. Reads
   * `field.hide` too so a future condition-driven hide is covered the same way.
   */
  isSubfieldHidden(subField: FormlyFieldConfig): boolean {
    return !!subField.hide || (subField.className ?? '').split(/\s+/).includes('hidden');
  }

  /** A choice from the menu is a user edit: marks the control dirty + touched (user-edit.util). */
  selectType(value: string): void {
    commitUserValue(this.typeField()?.formControl, value);
  }

  /** Closing the menu without choosing counts as leaving the control, so a required error can show. */
  onTypeMenuClosed(): void {
    this.typeField()?.formControl?.markAsTouched();
  }
}
